// Design agent: Optimises the hierarchical planner with shared normalisers and styling helpers.
// Developer agent: Aligns slider state, list behaviour, and bit gradients across IPv4 tools.
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  alignToBlock,
  computeSubnetPrefixForHosts,
  decomposeFreeSpace,
  formatMask,
  getTotalAddressCount,
  getUsableHostCount,
  intToIpv4,
  parseCidr,
} from '../utils/ipMath';
import { createBitGradientStyle } from '../utils/bitStyling';
import { normaliseWithTail } from '../utils/listNormalization';
import { useKeyboardLayout } from '../utils/useKeyboardLayout';
import HierarchicalSlider from './HierarchicalSlider';
import MethodToggle from './MethodToggle';
import PoolUsage from './PoolUsage';
import SliderZoomOverlay from './SliderZoomOverlay';
// Design agent: Spec 010 — reuses the exact focus/scroll and "↵ Enter" hint building blocks that
// IpInputField exposes, so the host-count row (a plain <input>, not an IpInputField) matches the
// inspector's behaviour instead of a re-implemented copy drifting out of sync.
import { EnterHintChip, scrollFieldIntoViewOnFirstCharacter } from './IpInputField';
import ExportMenu from './ExportMenu';

const MAX_SUBNET_FIELDS = 16;

// Design agent: Spec 013 — column order/labels for method 1's CSV/TSV export, matching the spec's
// "VLSM CSV columns" list exactly.
const VLSM_CSV_HEADERS = [
  'Name',
  'Hosts requested',
  'Network',
  'Prefix',
  'Mask',
  'First host',
  'Last host',
  'Usable',
  'Wasted addresses',
];
const MAX_MULTIPLIER = 64;
// Design agent: Sets a generous cap for slider layers while still keeping the UI manageable.
const MAX_LAYER_COUNT = 12;
// Design agent: Establishes the allowed CIDR range for slider interaction.
const MAX_SLIDER_PREFIX = 32;
const MIN_SLIDER_PREFIX = 0;
// Design agent: Defines magnetic breakpoints that help users land on popular CIDR boundaries.
const MAGNET_PREFIXES = [16, 24, 32];
// Design agent: Mirrors the slider gradient so list summaries can reuse layer colours.
const LAYER_PALETTE = ['var(--layer-color-a)', 'var(--layer-color-b)', 'var(--layer-color-c)', 'var(--layer-color-d)'];
// Design agent: Shares the network/host highlight colors with the inspector tooltip.
const CIDR_COLORS = { network: 'var(--layer-color-a)', host: 'var(--layer-host-color)' };

// Design agent: Splits a 32-bit address into its dotted-decimal octets.
const toOctets = (value) => [
  (value >>> 24) & 0xff,
  (value >>> 16) & 0xff,
  (value >>> 8) & 0xff,
  value & 0xff,
];

// Design agent: Builds algebraic tokens that describe how each layer contributes to every octet.
const buildAddressFormulaOctets = (parsedSupernet, layers) => {
  if (!parsedSupernet) {
    return ['0', '0', '0', '0'];
  }
  const baseOctets = toOctets(parsedSupernet.network);
  const formulaTerms = baseOctets.map((value) => (value === 0 ? [] : [value.toString()]));

  layers.forEach((layer, index) => {
    const rawLabel = (layer.label ?? `Layer ${index + 1}`).trim();
    const label = rawLabel === '' ? `Layer ${index + 1}` : rawLabel;
    const blockSize = 2 ** (MAX_SLIDER_PREFIX - layer.prefix);
    const deltas = toOctets(blockSize);
    deltas.forEach((delta, octetIndex) => {
      if (delta === 0) {
        return;
      }
      const token = delta === 1 ? label : `${delta}×${label}`;
      formulaTerms[octetIndex].push(token);
    });
  });

  return formulaTerms.map((terms) => (terms.length === 0 ? '0' : terms.join(' + ')));
};

// Design agent: Maps each bit across the address to either the base network, a layer, or the host remainder.
const buildLayerBitPalette = (parsedSupernet, layers) => {
  const palette = new Array(MAX_SLIDER_PREFIX).fill(CIDR_COLORS.host);
  let cursor = 0;

  if (parsedSupernet) {
    const baseBits = Math.max(0, Math.min(MAX_SLIDER_PREFIX, parsedSupernet.prefix));
    for (let bit = 0; bit < baseBits; bit += 1) {
      palette[bit] = CIDR_COLORS.network;
    }
    cursor = Math.max(cursor, baseBits);
  }

  layers.forEach((layer, index) => {
    const targetPrefix = Math.max(0, Math.min(MAX_SLIDER_PREFIX, layer.prefix));
    const span = Math.max(0, targetPrefix - cursor);
    const colour = LAYER_PALETTE[index % LAYER_PALETTE.length];
    for (let bit = 0; bit < span; bit += 1) {
      const paletteIndex = cursor + bit;
      if (paletteIndex >= MAX_SLIDER_PREFIX) {
        break;
      }
      palette[paletteIndex] = colour;
    }
    cursor = Math.max(cursor, targetPrefix);
  });

  return palette;
};

// Design agent: Produces coloured CIDR segments for an address-preview display.
// Developer agent: Returns stable segment objects that downstream memoised views can reuse.
const buildExampleSegments = (address, prefix) => {
  const octets = intToIpv4(address).split('.');
  const fullNetworkOctets = Math.floor(prefix / 8);
  const partialBits = prefix % 8;

  return octets.flatMap((octet, index) => {
    const segments = [];
    if (index < fullNetworkOctets) {
      segments.push({
        key: `octet-${index}`,
        value: octet,
        style: { color: CIDR_COLORS.network },
        role: 'network',
      });
    } else if (index === fullNetworkOctets && partialBits > 0) {
      const bitSlice = [];
      for (let bit = 0; bit < 8; bit += 1) {
        bitSlice.push(bit < partialBits ? CIDR_COLORS.network : CIDR_COLORS.host);
      }
      segments.push({
        key: `octet-${index}`,
        value: octet,
        style: createBitGradientStyle(bitSlice, CIDR_COLORS.host),
        role: 'mixed',
      });
    } else {
      segments.push({
        key: `octet-${index}`,
        value: octet,
        style: { color: CIDR_COLORS.host },
        role: 'host',
      });
    }

    if (index < octets.length - 1) {
      segments.push({
        key: `dot-${index}`,
        value: '.',
        style: { color: 'inherit' },
        role: 'separator',
      });
    }
    return segments;
  });
};

// Design agent: Applies the palette across octets so each layer colour remains visible in the formula.
// Developer agent: Trims long formula tokens and carries gradient metadata for responsive layouts.
const buildFormulaSegments = (octets, bitPalette) =>
  octets.map((octet, index) => {
    const trimmed = octet.length > 15 ? `${octet.slice(0, 15)}...` : octet;
    const start = index * 8;
    const slice = bitPalette ? bitPalette.slice(start, start + 8) : [];
    return {
      key: `formula-octet-${index}`,
      value: trimmed,
      style: createBitGradientStyle(slice, CIDR_COLORS.host),
    };
  });

// Design agent: Splits the formula across rows and reports layout metrics for responsive styling.
const buildFormulaRows = (segments) => {
  if (!segments || segments.length === 0) {
    return { rows: [], maxLength: 0 };
  }

  const totalLength = segments.reduce((sum, segment) => sum + segment.value.length, 0) + (segments.length - 1);
  const needsSplit = totalLength > 32;
  const groups = needsSplit ? [[0, 1], [2, 3]] : [[0, 1, 2, 3]];
  const rows = [];
  let longest = 0;

  groups.forEach((indices, groupIndex) => {
    if (!indices || indices.length === 0) {
      return;
    }
    const rowSegments = [];
    let rowLength = 0;
    indices.forEach((octetIndex, localIndex) => {
      const segment = segments[octetIndex];
      if (!segment) {
        return;
      }
      rowSegments.push(segment);
      rowLength += segment.value.length;
      const isLastOverall = octetIndex === segments.length - 1;
      const isLastInGroup = localIndex === indices.length - 1;
      if (!isLastOverall && (!isLastInGroup || groupIndex < groups.length - 1)) {
        rowSegments.push({
          key: `formula-dot-${octetIndex}`,
          value: '.',
          style: { color: 'inherit' },
        });
        rowLength += 1;
      }
    });
    if (rowSegments.length > 0) {
      rows.push(rowSegments);
      longest = Math.max(longest, rowLength);
    }
  });

  return { rows, maxLength: longest };
};

// Design agent: Normalises subnet rows to keep a single empty trailing entry with defaults.
// Developer agent: Delegates to the shared list normaliser to mirror behaviour across tools.
const normaliseHostRows = (rows) =>
  normaliseWithTail(rows, {
    maxItems: MAX_SUBNET_FIELDS,
    createEmpty: () => ({ hosts: '', multiplier: '', name: '' }),
    cleanValue: (row) => {
      const hosts = typeof row?.hosts === 'string' ? row.hosts : '';
      const multiplierRaw = typeof row?.multiplier === 'string' ? row.multiplier : '';
      const trimmedMultiplier = multiplierRaw.trim();
      const name = typeof row?.name === 'string' ? row.name : '';
      return {
        hosts,
        multiplier: trimmedMultiplier === '' ? '' : multiplierRaw,
        name,
      };
    },
    isEmpty: (row) => {
      const hosts = typeof row?.hosts === 'string' ? row.hosts : '';
      return hosts.trim() === '';
    },
  });

// Design agent: Produces a mask integer from a CIDR prefix length.
const maskFromPrefix = (prefix) => {
  if (prefix === 0) {
    return 0;
  }
  return (~0 << (32 - prefix)) >>> 0;
};

// Design agent: Spec 011 — picks the layer handles a fresh mount should start with: whatever was
// restored from the URL/localStorage (persistence.js already validated the shape) if present,
// otherwise the same single default handle a brand-new visit has always started with.
// Developer agent: Kept as a standalone pure function (rather than inlined at both call sites) so
// the two lazy initialisers that need it (layerHandles' useState and handleIdRef's useRef, above)
// can call it independently without one depending on the other's result.
const resolveInitialLayerHandles = (initialState) =>
  Array.isArray(initialState?.layerHandles) && initialState.layerHandles.length > 0
    ? initialState.layerHandles
    : [{ id: 'layer-0', prefix: 24, label: 'Layer 1' }];

// Design agent: Spec 011 — restored layer handles can carry ids like "layer-3" from a much longer
// previous session; createHandle's own counter must resume above the highest id already in use so a
// newly added handle can never collide with a restored one.
// Developer agent: Ids are always formatted "layer-<n>" (see createHandle and persistence.js's
// decoder), so this simply parses the trailing number out of each id and returns one past the
// largest found — falling back to the handle count when no id parses, which matches the plain
// `useRef(1)` behaviour a single default handle ("layer-0") always had.
const computeNextHandleId = (handles) => {
  const highestSuffix = handles.reduce((highest, handle) => {
    const match = typeof handle?.id === 'string' ? /^layer-(\d+)$/.exec(handle.id) : null;
    const suffix = match ? Number(match[1]) : -1;
    return Number.isFinite(suffix) && suffix > highest ? suffix : highest;
  }, -1);
  return highestSuffix >= 0 ? highestSuffix + 1 : handles.length;
};

// Design agent: Keeps layer handles ordered and within the valid slider range.
const sanitiseHandles = (handles, basePrefix) => {
  const minimum = Math.min(MAX_SLIDER_PREFIX, Math.max(basePrefix, MIN_SLIDER_PREFIX));
  const normalisedHandles = handles
    .filter((handle) => handle && Number.isFinite(handle.prefix))
    .map((handle) => ({
      ...handle,
      prefix: Math.round(handle.prefix),
    }));

  if (normalisedHandles.length === 0) {
    return [];
  }

  const clipped = normalisedHandles.map((handle) => ({
    ...handle,
    prefix: Math.min(MAX_SLIDER_PREFIX, Math.max(handle.prefix, minimum)),
  }));

  const sorted = [...clipped].sort((a, b) => {
    if (a.prefix === b.prefix) {
      return a.id.localeCompare(b.id);
    }
    return a.prefix - b.prefix;
  });

  return sorted.slice(0, MAX_LAYER_COUNT);
};

// Design agent: Summarises the number of networks produced per layer and the remaining host pool.
const buildSliderPlan = (parsedSupernet, handles) => {
  const minPrefix = Math.min(MAX_SLIDER_PREFIX, Math.max(parsedSupernet.prefix, MIN_SLIDER_PREFIX));
  const orderedHandles = sanitiseHandles(handles, parsedSupernet.prefix);
  const layers = [];

  let previousPrefix = minPrefix;

  orderedHandles.forEach((handle, index) => {
    const bits = Math.max(0, handle.prefix - previousPrefix);
    const networkCount = 2 ** bits;
    const addressesPerNetwork = 2 ** (MAX_SLIDER_PREFIX - handle.prefix);
    // Design agent: Flags octet-aligned layers so path offsets can respect readability cues.
    // Developer agent: Exposes readability metadata for downstream explorer logic.
    const isReadable = handle.prefix % 8 === 0;
    layers.push({
      bits,
      prefix: handle.prefix,
      id: handle.id,
      index,
      label: handle.label ?? `Layer ${index + 1}`,
      networkCount,
      addressesPerNetwork,
      isReadable,
    });
    previousPrefix = handle.prefix;
  });

  const hostBits = Math.max(0, MAX_SLIDER_PREFIX - previousPrefix);
  const hostAddresses = 2 ** hostBits;
  const hostUsable = hostBits >= 2 ? Math.max(0, hostAddresses - 2) : hostAddresses;

  return {
    orderedHandles,
    basePrefix: minPrefix,
    layers,
    host: {
      prefix: previousPrefix,
      hostBits,
      hostAddresses,
      hostUsable,
    },
  };
};

// Design agent: Implements hierarchical IPv4 planning with multiplier support and path-based schemes.
// Design agent: Spec 011 — accepts an optional `initialState` (exactly the `vlsm` slice shape
// src/utils/persistence.js decodes: baseNetwork, hostInputs, gapSize, allowPointToPointLinks, mode,
// pathSupernet, layerHandles) so App can restore a reload/shared link, and an `onStateChange`
// callback fired whenever any of those fields change so App can persist them. Both are optional so
// VlsmCalculator keeps working unchanged when mounted standalone (e.g. in isolated tests).
function VlsmCalculator({ initialState, onStateChange } = {}) {
  const [baseNetwork, setBaseNetwork] = useState(() => initialState?.baseNetwork ?? '');
  // Design agent: Start with empty multiplier; treat blank as 1 internally, but do not prefill visually.
  // Developer agent: Restores the full row list when initialState provides one, otherwise the single
  // blank row every fresh visit already started with; either way normaliseHostRows enforces the
  // "always one trailing blank row" invariant on the very next edit.
  const [hostInputs, setHostInputs] = useState(() => {
    const restoredRows = initialState?.hostInputs;
    return Array.isArray(restoredRows) && restoredRows.length > 0
      ? normaliseHostRows(restoredRows)
      : [{ hosts: '', multiplier: '', name: '' }];
  });
  // Design agent: Spec 010 — tracks which host-count row currently has focus so its "↵ Enter" hint
  // chip can be shown only for that one row, mirroring the inspector's activeEntryIndex pattern even
  // though this row is a plain <input> rather than an IpInputField.
  // Developer agent: -1 means no row is focused; set/cleared from the hosts input's onFocus/onBlur.
  const [activeHostIndex, setActiveHostIndex] = useState(-1);
  // Design agent: Adds a global gap setting to space out subnets.
  const [gapSize, setGapSize] = useState(() => initialState?.gapSize ?? '');
  // Design agent: Spec 002 — lets the operator size 1-2 host requests as an RFC 3021 /31 link
  // instead of the traditional /30. Defaults to ON since point-to-point links are the common case
  // for router-to-router subnets. Spec 011 — now restored from initialState (persisted as the `p2p`
  // URL param / localStorage field) instead of always defaulting to true.
  const [allowPointToPointLinks, setAllowPointToPointLinks] = useState(() =>
    typeof initialState?.allowPointToPointLinks === 'boolean' ? initialState.allowPointToPointLinks : true
  );
  const [mode, setMode] = useState(() => (initialState?.mode === 'method2' ? 'method2' : 'method1'));
  const [pathSupernet, setPathSupernet] = useState(() => initialState?.pathSupernet ?? '');
  // Design agent: Spec 011 — restored layer handles can carry ids like "layer-3" from a previous
  // session; seeding the counter from computeNextHandleId (rather than always 1) guarantees the next
  // handle created by createHandle below never collides with a restored id.
  // Developer agent: useRef (unlike useState) has no lazy-initialiser form — its argument is
  // evaluated on every render, but only actually used to seed `.current` on the very first one, so
  // recomputing this cheap, pure helper on later renders costs nothing functionally. useState's own
  // initialiser below is the lazy form, so it truly only runs once.
  const handleIdRef = useRef(computeNextHandleId(resolveInitialLayerHandles(initialState)));
  const [layerHandles, setLayerHandles] = useState(() => resolveInitialLayerHandles(initialState));
  // Design agent: Stores the selected network index per layer for the IP example helper.
  const [layerExampleInputs, setLayerExampleInputs] = useState(['1']);
  // Design agent: Tracks the host index requested in the IP example helper.
  const [exampleHostIndex, setExampleHostIndex] = useState('1');
  // Design agent: Allows path explorers to apply readability-based offsets when desired.
  // Developer agent: Stores whether octet-aligned layers should shift by an extra network.
  const [applyReadableOffsets, setApplyReadableOffsets] = useState(false);
  // Design agent: Spec 009 — whether the method-2 slider's fullscreen zoom overlay is showing.
  // Deliberately plain UI state (not persisted): it only controls which surface renders the same
  // slider/layer/example data that already lives in the state above.
  const [isSliderZoomOpen, setIsSliderZoomOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState([]);
  const inputRefs = useRef([]);
  // Design agent: Spec 008 — the VLSM view's own root element, used only to check (via the closest
  // ".spatial-view" ancestor's class list) whether this view is the one currently shown in the
  // App's card stack, so the Alt+1/Alt+2/Alt+M shortcuts stay scoped to the plan tab.
  const vlsmRootRef = useRef(null);
  // Design agent: Spec 008 — remembers the supernet value that switching methods last copied across,
  // so a later switch can tell "the field still holds what we synced" (safe to re-sync) apart from
  // "the user typed something else since" (never overwrite that). A ref, not state: this bookkeeping
  // does not need to trigger a re-render on its own.
  const syncedSupernetValueRef = useRef(null);
  // Design agent: Spec 008 — reports the detected keyboard layout so the MethodToggle shortcut hint
  // can be layout-aware (see buildShortcutTooltip below) instead of assuming QWERTY.
  const detectedKeyboardLayout = useKeyboardLayout();

  // Design agent: Parses the supernet once for downstream consumers.
  const parsedSupernet = useMemo(() => parseCidr(pathSupernet), [pathSupernet]);

  // Design agent: Creates uniquely identified slider handles with a desired prefix.
  // Design agent: Generates uniquely identified handles while preserving user-friendly labels.
  const createHandle = (prefix, label) => {
    const id = `layer-${handleIdRef.current}`;
    handleIdRef.current += 1;
    return { id, prefix, label: label ?? `Layer ${handleIdRef.current}` };
  };

  // Design agent: Revalidates slider layers whenever the supernet changes.
  useEffect(() => {
    if (!parsedSupernet) {
      return;
    }
    setLayerHandles((previous) => {
      const sanitised = sanitiseHandles(previous, parsedSupernet.prefix);
      const ensured =
        sanitised.length === 0
          ? [
            createHandle(
              Math.min(MAX_SLIDER_PREFIX, Math.max(parsedSupernet.prefix, MIN_SLIDER_PREFIX)),
              'Layer 1',
            ),
          ]
          : sanitised;
      if (ensured.length !== previous.length) {
        return ensured;
      }
      for (let index = 0; index < ensured.length; index += 1) {
        const nextHandle = ensured[index];
        const previousHandle = previous[index];
        if (!previousHandle || previousHandle.id !== nextHandle.id || previousHandle.prefix !== nextHandle.prefix) {
          return ensured;
        }
      }
      return previous;
    });
  }, [parsedSupernet]);

  // Design agent: Keeps the IP example layer selections in sync with the slider layer count.
  useEffect(() => {
    setLayerExampleInputs((previous) => {
      const desiredLength = Math.max(0, layerHandles.length);
      if (desiredLength === previous.length) {
        return previous;
      }
      const trimmed = previous.slice(0, desiredLength);
      while (trimmed.length < desiredLength) {
        trimmed.push('1');
      }
      if (trimmed.length === previous.length && trimmed.every((value, index) => value === previous[index])) {
        return previous;
      }
      return trimmed;
    });
  }, [layerHandles.length]);

  // Design agent: Spec 011 — reports the persistable slice of this component's own state (the seven
  // fields listed in initialState's shape above) every time any of them changes, so App can debounce
  // a write to localStorage/the URL without VlsmCalculator knowing anything about persistence.
  // Deliberately excludes activeHostIndex, layerExampleInputs, exampleHostIndex,
  // applyReadableOffsets, isSliderZoomOpen and expandedGroups — all transient UI/example-helper state
  // that a reload is not expected to restore (mirrors the "Deliberately plain UI state (not
  // persisted)" note already on isSliderZoomOpen above).
  // Developer agent: Guards against a missing callback so VlsmCalculator keeps working unchanged
  // when mounted without a parent wiring this prop (e.g. in isolated tests).
  useEffect(() => {
    if (typeof onStateChange !== 'function') {
      return;
    }
    onStateChange({
      baseNetwork,
      hostInputs,
      gapSize,
      allowPointToPointLinks,
      mode,
      pathSupernet,
      layerHandles,
    });
  }, [baseNetwork, hostInputs, gapSize, allowPointToPointLinks, mode, pathSupernet, layerHandles, onStateChange]);

  // Design agent: Focuses a subnet host field and places the caret at the end.
  // Developer agent: Spec 010 — passes `preventScroll: true` so Enter/Backspace auto-advance between
  // rows never triggers the browser's own native scroll-into-view; only the explicit, centred
  // scrollFieldIntoViewOnFirstCharacter call (wired through handleHostChange) is allowed to scroll
  // this list, and only on that field's first keystroke. Falls back to a plain focus() call for
  // environments/tests where the options overload is not implemented.
  const focusHostField = (index) => {
    queueMicrotask(() => {
      const element = inputRefs.current[index];
      if (element) {
        const length = element.value.length;
        try {
          element.focus({ preventScroll: true });
        } catch (error) {
          element.focus();
        }
        element.setSelectionRange(length, length);
      }
    });
  };

  // Design agent: Checks if inputs are strictly descending to offer a sort shortcut.
  const inputsAreSorted = useMemo(() => {
    let lastSize = Infinity;
    for (const entry of hostInputs) {
      const raw = entry.hosts;
      // Skip empty or invalid entries during the check
      if (!raw || raw.trim() === '') continue;
      const size = parseInt(raw, 10);
      if (isNaN(size)) continue;
      if (size > lastSize) return false;
      lastSize = size;
    }
    return true;
  }, [hostInputs]);

  // Design agent: Reorders inputs to be largest-first, handling the gaps.
  const handleManualSort = () => {
    setHostInputs((prev) => {
      const sorted = [...prev].sort((a, b) => {
        const valA = parseInt(a.hosts, 10) || 0;
        const valB = parseInt(b.hosts, 10) || 0;
        // Sort descending (largest hosts first)
        return valB - valA;
      });
      return normaliseHostRows(sorted);
    });
  };

  // Design agent: Computes addressing plans for method 1 (host-driven VLSM).
  const methodOneResults = useMemo(() => {
    const parsedBase = parseCidr(baseNetwork);
    if (!parsedBase) {
      if (baseNetwork.trim() === '') {
        return { pending: true };
      }
      return { error: 'Invalid base network (use format x.x.x.x/yy).' };
    }

    const usableCapacity = getUsableHostCount(parsedBase.prefix);
    const networkCapacity = getTotalAddressCount(parsedBase.prefix);
    const groups = hostInputs.map((entry, index) => ({
      index,
      hostsRaw: entry.hosts,
      multiplierRaw: entry.multiplier,
      name: entry.name,
    }));

    const activeGroups = groups.filter((group) => group.hostsRaw.trim() !== '');
    if (activeGroups.length === 0) {
      return {
        pending: true,
        parsedBase,
        usableCapacity,
      };
    }

    const expanded = [];
    for (const group of activeGroups) {
      const hosts = Number(group.hostsRaw);
      if (Number.isNaN(hosts) || hosts < 0) {
        return { error: `Invalid host count in subnet ${group.index + 1}.` };
      }
      const multiplier = group.multiplierRaw.trim() === '' ? 1 : Number(group.multiplierRaw);
      if (Number.isNaN(multiplier) || multiplier < 1) {
        return { error: `Multiplier in subnet ${group.index + 1} must be at least 1.` };
      }
      if (multiplier > MAX_MULTIPLIER) {
        return { error: `Multiplier in subnet ${group.index + 1} is too large (max ${MAX_MULTIPLIER}).` };
      }

      for (let copy = 0; copy < multiplier; copy += 1) {
        expanded.push({
          groupIndex: group.index,
          instance: copy,
          hosts,
        });
      }
    }

    const sorted = [...expanded];

    const baseStart = parsedBase.network;
    const baseEnd = baseStart + networkCapacity;
    let cursor = baseStart;

    // Design agent: Parse the global gap to apply between networks.
    const parsedGap = parseInt(gapSize, 10);
    const gap = (Number.isNaN(parsedGap) || parsedGap < 0) ? 0 : parsedGap;

    const allocationsByGroup = hostInputs.map(() => []);
    const gaps = [];
    let totalWaste = 0;
    // Design agent: Spec 007 — every stretch of unallocated addresses (an alignment gap between two
    // subnets, or the tail left after the last one) is recorded so the pool usage panel can later
    // decompose it into aligned CIDR blocks ("still fits: 1 × /26").
    const freeRegions = [];
    // Design agent: Spec 007 — when a request cannot be placed, the plan used to hard-fail the whole
    // computation. Instead we now skip that single request (it simply is not allocated) and keep
    // trying the remaining ones from the same cursor position, while tallying how many addresses
    // were short so the pool usage panel can report "Exceeds pool by N addresses".
    let overflowAddresses = 0;
    let overflowCount = 0;

    for (const request of sorted) {
      // Design agent: Spec 002 — delegate the sizing rule to the shared helper so the /31
      // point-to-point exception (RFC 3021) and the legacy "hosts + 2" formula live in one place.
      const prefix = computeSubnetPrefixForHosts(request.hosts, allowPointToPointLinks);
      const blockSize = 2 ** (32 - prefix);

      let alignedStart = cursor;
      if ((cursor - baseStart) % blockSize !== 0) {
        alignedStart = alignToBlock(cursor, blockSize, baseStart);
        const currentGap = alignedStart - cursor;
        if (currentGap > 0) {
          gaps.push(currentGap);
          totalWaste += currentGap;
          freeRegions.push({ start: cursor, end: alignedStart });
        }
      }

      if (alignedStart + blockSize > baseEnd) {
        overflowAddresses += blockSize;
        overflowCount += 1;
        // Design agent: Do not advance the cursor — a later, smaller request in the list may still
        // fit in the space that remains before the supernet's end.
        continue;
      }

      allocationsByGroup[request.groupIndex][request.instance] = {
        prefix,
        network: alignedStart,
        broadcast: alignedStart + blockSize - 1,
        blockSize,
        hosts: request.hosts,
      };
      // Design agent: Advance cursor by block size plus the optional gap.
      cursor = alignedStart + blockSize + gap;
    }

    // Design agent: Spec 007 — whatever is left between the last allocation and the supernet's end
    // is also free space that could still host another subnet.
    if (cursor < baseEnd) {
      freeRegions.push({ start: cursor, end: baseEnd });
    }

    const firstUsable = parsedBase.prefix >= 31 ? parsedBase.network : parsedBase.network + 1;
    const lastUsable = parsedBase.prefix >= 31 ? parsedBase.broadcast : parsedBase.broadcast - 1;

    // Design agent: Spec 007 — the pool usage panel needs the raw address accounting (allocated vs
    // remaining vs supernet size) plus the free space decomposed into aligned CIDR blocks. All of it
    // is derived here, once, rather than recomputed inside the presentational PoolUsage component.
    const allocatedAddresses = allocationsByGroup
      .flat()
      .filter(Boolean)
      .reduce((sum, allocation) => sum + allocation.blockSize, 0);
    const freeBlocks = freeRegions.flatMap((region) => decomposeFreeSpace(region.start, region.end));
    const remainingAddresses = freeBlocks.reduce((sum, block) => sum + block.blockSize, 0);

    return {
      parsedBase,
      usableCapacity,
      usableRange: {
        start: intToIpv4(firstUsable),
        end: intToIpv4(lastUsable),
      },
      totalSubnets: sorted.length,
      gaps,
      totalWaste,
      groups: hostInputs.map((input, index) => ({
        allocations: allocationsByGroup[index]?.filter(Boolean) ?? [],
        name: input.name,
      })),
      poolUsage: {
        networkCapacity,
        allocatedAddresses,
        remainingAddresses,
        usagePercent: networkCapacity > 0 ? (allocatedAddresses / networkCapacity) * 100 : 0,
        freeBlocks,
        overflowAddresses,
        overflowCount,
      },
    };
  }, [baseNetwork, hostInputs, gapSize, allowPointToPointLinks]);

  // Design agent: Spec 013 — flattens method 1's per-group allocations into the "name, hosts
  // requested, network, prefix, mask, first host, last host, usable, wasted addresses" row shape the
  // spec's VLSM CSV columns call for. Left as an empty array whenever method 1 has no valid plan yet
  // (pending input, an error, or the operator is on method 2) so the Export menu's CSV/BIND buttons
  // simply disable themselves rather than exporting stale or partial data.
  // Developer agent: A group can expand to more than one allocation when its multiplier is above 1;
  // each allocation becomes its own row, suffixed "#2", "#3", ... past the first so duplicate names
  // stay distinguishable in the exported sheet.
  const vlsmExportSubnets = useMemo(() => {
    if (methodOneResults.pending || methodOneResults.error || !Array.isArray(methodOneResults.groups)) {
      return [];
    }
    const subnets = [];
    methodOneResults.groups.forEach((group, groupIndex) => {
      const baseName = group.name && group.name.trim() !== '' ? group.name.trim() : `Subnet ${groupIndex + 1}`;
      group.allocations.forEach((allocation, instanceIndex) => {
        const displayName = group.allocations.length > 1 ? `${baseName} #${instanceIndex + 1}` : baseName;
        const usable = getUsableHostCount(allocation.prefix);
        subnets.push({
          name: displayName,
          hostsRequested: allocation.hosts,
          network: allocation.network,
          prefix: allocation.prefix,
          mask: formatMask(maskFromPrefix(allocation.prefix)),
          firstHost: allocation.prefix >= 31 ? allocation.network : allocation.network + 1,
          lastHost: allocation.prefix >= 31 ? allocation.broadcast : allocation.broadcast - 1,
          usable,
          // Design agent: "Wasted addresses" reports how much of the allocated block was not
          // actually needed by the request — the operator asked for N hosts but the smallest
          // aligned block that fits N always rounds up to the next power of two.
          wasted: Math.max(0, usable - allocation.hosts),
        });
      });
    });
    return subnets;
  }, [methodOneResults]);

  // Design agent: Spec 013 — the CSV/TSV rows for the VLSM Export menu, derived from the same
  // flattened subnet list the BIND export uses so both formats always agree with each other.
  const vlsmExportRows = useMemo(() => {
    return vlsmExportSubnets.map((subnet) => [
      subnet.name,
      subnet.hostsRequested,
      intToIpv4(subnet.network),
      subnet.prefix,
      subnet.mask,
      intToIpv4(subnet.firstHost),
      intToIpv4(subnet.lastHost),
      subnet.usable,
      subnet.wasted,
    ]);
  }, [vlsmExportSubnets]);

  // Design agent: Spec 007 — the pool usage panel (supernet size / allocated / remaining / "still
  // fits") is NOT reproduced for method 2. Method 2's sliders are clamped between the supernet's own
  // prefix and /32 (see sanitiseHandles), so every layer boundary always exactly partitions the
  // supernet with zero gaps and zero overflow by construction — there is no "free space" or
  // "exceeds pool" state to report the way method 1 has after its host-driven, potentially
  // non-contiguous allocation. Reusing PoolUsage here would always show "100% used, nothing left",
  // which carries no information. Left out of scope per spec 007 bullet 4 rather than added as a
  // panel that never says anything new.
  // Design agent: Computes addressing plans derived from the slider-based hierarchy.
  const methodTwoPlan = useMemo(() => {
    if (pathSupernet.trim() === '') {
      return { pending: true };
    }

    if (!parsedSupernet) {
      return { error: 'Invalid supernet (use format x.x.x.x/yy).' };
    }

    const sliderPlan = buildSliderPlan(parsedSupernet, layerHandles);

    return {
      parsedBase: parsedSupernet,
      ...sliderPlan,
    };
  }, [layerHandles, pathSupernet, parsedSupernet]);

  // Design agent: Provides reliable slider props even while the plan is pending or invalid.
  const sliderPreview = useMemo(() => {
    const fallbackPrefix = parsedSupernet ? parsedSupernet.prefix : MIN_SLIDER_PREFIX;
    const basePrefix = methodTwoPlan?.basePrefix ?? Math.min(MAX_SLIDER_PREFIX, Math.max(fallbackPrefix, MIN_SLIDER_PREFIX));
    const handles = methodTwoPlan?.orderedHandles ?? sanitiseHandles(layerHandles, fallbackPrefix);
    return {
      basePrefix,
      handles,
    };
  }, [layerHandles, methodTwoPlan, parsedSupernet]);

  // Design agent: Prepares the algebraic string used to describe the addressing pattern.
  const addressFormulaOctets = useMemo(() => {
    if (!methodTwoPlan || methodTwoPlan.error || methodTwoPlan.pending) {
      return null;
    }
    return buildAddressFormulaOctets(methodTwoPlan.parsedBase, methodTwoPlan.layers);
  }, [methodTwoPlan]);

  // Design agent: Mirrors layer ownership across the 32-bit palette for styling.
  const addressFormulaPalette = useMemo(() => {
    if (!methodTwoPlan || methodTwoPlan.error || methodTwoPlan.pending) {
      return null;
    }
    return buildLayerBitPalette(methodTwoPlan.parsedBase, methodTwoPlan.layers);
  }, [methodTwoPlan]);

  // Design agent: Converts the algebraic octets into coloured CIDR segments.
  const addressFormulaSegments = useMemo(() => {
    if (!addressFormulaOctets) {
      return [];
    }
    return buildFormulaSegments(addressFormulaOctets, addressFormulaPalette ?? []);
  }, [addressFormulaOctets, addressFormulaPalette]);

  // Design agent: Provides pre-split rows for responsive rendering.
  const addressFormulaLayout = useMemo(() => buildFormulaRows(addressFormulaSegments), [addressFormulaSegments]);
  const addressFormulaRows = addressFormulaLayout.rows;

  // Design agent: Chooses a comfortable font size based on the widest formula row.
  const addressFormulaFontSize = useMemo(() => {
    const length = addressFormulaLayout.maxLength || 0;
    if (length <= 28) {
      return '1.1rem';
    }
    if (length <= 36) {
      return '1rem';
    }
    if (length <= 44) {
      return '0.94rem';
    }
    return '0.88rem';
  }, [addressFormulaLayout]);

  // Design agent: Evaluates the concrete IP example based on layer and host selections.
  const exampleAnalysis = useMemo(() => {
    if (!methodTwoPlan || methodTwoPlan.error || methodTwoPlan.pending) {
      return null;
    }

    const layers = methodTwoPlan.layers;
    const selections = layers.map((layer, index) => {
      const raw = layerExampleInputs[index] ?? '';
      const parsed = Number(raw);
      if (Number.isNaN(parsed) || parsed < 1) {
        return 1;
      }
      return Math.min(parsed, layer.networkCount);
    });

    const hostCapacity =
      layers.length > 0 ? layers[layers.length - 1].addressesPerNetwork : methodTwoPlan.host.hostAddresses;
    const rawHost = Number(exampleHostIndex);
    const clampedHost = Number.isNaN(rawHost) || rawHost < 1 ? 1 : Math.min(rawHost, hostCapacity);

    let address = methodTwoPlan.parsedBase.network;
    const layerSummaries = [];

    selections.forEach((selection, index) => {
      const layer = layers[index];
      if (!layer) {
        return;
      }
      const blockSize = 2 ** (MAX_SLIDER_PREFIX - layer.prefix);
      const readableOffset = applyReadableOffsets && layer.isReadable ? 1 : 0;
      const effectiveSelection = Math.min(selection + readableOffset, layer.networkCount);
      const increment = Math.max(0, (effectiveSelection - 1) * blockSize);
      address += increment;
      layerSummaries.push({
        selection,
        effectiveSelection,
        appliedOffset: effectiveSelection - selection,
        prefix: layer.prefix,
        networkAddress: address,
        formattedNetwork: intToIpv4(address),
      });
    });

    const networkAddress = address;
    const finalPrefix = layers.length > 0 ? layers[layers.length - 1].prefix : methodTwoPlan.host.prefix;
    const hostOffsetCapacity = Math.max(0, hostCapacity - 1);
    const resolvedHostOffset = hostCapacity <= 1 ? 0 : Math.min(clampedHost, hostOffsetCapacity);
    const hostAddress = networkAddress + resolvedHostOffset;

    return {
      networkAddress,
      networkPrefix: finalPrefix,
      hostAddress,
      hostIndex: clampedHost,
      layerSummaries,
      hostCapacity,
      resolvedHostOffset,
    };
  }, [applyReadableOffsets, exampleHostIndex, layerExampleInputs, methodTwoPlan]);

  // Design agent: Produces coloured segments for the resolved example address.
  // Design agent: Produces coloured segments for the resolved example address.
  const exampleDisplaySegments = useMemo(() => {
    if (!exampleAnalysis) {
      return [];
    }
    return buildExampleSegments(exampleAnalysis.hostAddress, exampleAnalysis.networkPrefix);
  }, [exampleAnalysis]);

  // Design agent: Extracts concise KPIs for the refreshed method two metrics panel.
  // Design agent: Key Metrics removed per requirements.

  // Design agent: Updates the base network input for method 1.
  const handleBaseChange = (event) => {
    const value = event.target.value;
    const sanitized = value.replace(/[^0-9abcdef.:/ -]/gi, '');
    setBaseNetwork(sanitized);
  };

  // Design agent: Updates the global gap setting.
  const handleGapChange = (event) => {
    setGapSize(event.target.value.replace(/[^\d]/g, ''));
  };

  // Design agent: Spec 002 — flips the point-to-point /31 sizing rule on or off.
  // Developer agent: Syncs the checkbox state that drives computeSubnetPrefixForHosts.
  const handlePointToPointToggle = (event) => {
    setAllowPointToPointLinks(event.target.checked);
  };

  // Design agent: Spec 008 — switches between the two planning methods, and carries the current
  // supernet over into the method being switched to. The copy only happens when the target field is
  // empty or still holds exactly the value a previous switch synced into it — i.e. it protects
  // whatever the user deliberately typed into the other method's own field from ever being silently
  // overwritten.
  // Developer agent: `mode` here is always the method being left (the switch has not happened yet).
  const handleModeChange = (nextMode) => {
    if (nextMode === mode) {
      return;
    }

    const leavingModeSupernet = mode === 'method1' ? baseNetwork : pathSupernet;
    const enteringModeSupernet = nextMode === 'method1' ? baseNetwork : pathSupernet;
    const setEnteringModeSupernet = nextMode === 'method1' ? setBaseNetwork : setPathSupernet;

    const enteringFieldIsEmpty = enteringModeSupernet.trim() === '';
    const enteringFieldStillHoldsLastSync = enteringModeSupernet === syncedSupernetValueRef.current;
    const safeToCopySupernetAcross =
      (enteringFieldIsEmpty || enteringFieldStillHoldsLastSync) && leavingModeSupernet !== enteringModeSupernet;

    if (safeToCopySupernetAcross) {
      setEnteringModeSupernet(leavingModeSupernet);
      syncedSupernetValueRef.current = leavingModeSupernet;
    }

    setMode(nextMode);
  };

  // Design agent: Stores edits for host counts and multipliers.
  const handleHostChange = (index, field, value) => {
    if (field === 'hosts') {
      // Design agent: Spec 010 — the hosts field is a plain <input> (it does not render through
      // IpInputField), so it reuses the exact same empty -> non-empty scroll rule via the helper
      // shared with IpInputField instead of re-implementing it here.
      // Developer agent: Compares against the value still in state (i.e. before this update lands)
      // against the incoming one, then scrolls the actual DOM node already tracked in inputRefs.
      scrollFieldIntoViewOnFirstCharacter(hostInputs[index]?.hosts, value, inputRefs.current[index]);
    }
    setHostInputs((prev) => {
      const next = [...prev];
      const safeValue = field === 'multiplier' && value.trim() === '' ? '' : value;
      next[index] = { ...next[index], [field]: safeValue };
      return normaliseHostRows(next);
    });
  };

  // Design agent: Toggles whether a subnet group's allocations are expanded.
  const toggleGroupExpansion = (index) => {
    setExpandedGroups((prev) => {
      if (prev.includes(index)) {
        return prev.filter((value) => value !== index);
      }
      return [...prev, index];
    });
  };

  // Design agent: Provides keyboard navigation for subnet fields.
  const handleKeyDown = (event, index) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const nextIndex = Math.min(index + 1, hostInputs.length - 1);
      focusHostField(nextIndex);
      return;
    }

    if (event.key === 'Backspace' && hostInputs[index].hosts === '' && index > 0) {
      event.preventDefault();
      focusHostField(index - 1);
    }
  };

  // Design agent: Updates the supernet used in method 2.
  const handleSupernetChange = (event) => {
    const value = event.target.value;
    const sanitized = value.replace(/[^0-9abcdef.:/ -]/gi, '');
    setPathSupernet(sanitized);
  };

  // Design agent: Spec 009 — opens/closes the slider's fullscreen zoom overlay. Two tiny handlers
  // rather than passing setIsSliderZoomOpen directly so SliderZoomOverlay's onClose prop reads as
  // an intent ("close me") instead of a raw state setter.
  const handleOpenSliderZoom = () => setIsSliderZoomOpen(true);
  const handleCloseSliderZoom = () => setIsSliderZoomOpen(false);

  // Design agent: Lets planners quickly toggle readability offsets without leaving the explorer.
  // Developer agent: Syncs the checkbox state that drives per-layer +1 adjustments.
  const handleReadableOffsetChange = (event) => {
    setApplyReadableOffsets(event.target.checked);
  };

  // Design agent: Applies slider movements to a given layer handle.
  const handleLayerChange = (handleId, nextValue) => {
    setLayerHandles((prev) => {
      const basePrefix = parsedSupernet ? parsedSupernet.prefix : MIN_SLIDER_PREFIX;
      const updated = prev.map((handle) =>
        handle.id === handleId
          ? {
            ...handle,
            prefix: nextValue,
          }
          : handle,
      );
      const sanitised = sanitiseHandles(updated, basePrefix);
      if (sanitised.length === 0) {
        return [createHandle(Math.min(MAX_SLIDER_PREFIX, Math.max(basePrefix, MIN_SLIDER_PREFIX)), 'Layer 1')];
      }
      return sanitised;
    });
  };

  // Design agent: Inserts a new intermediate layer at a balanced position.
  const handleAddLayer = () => {
    setLayerHandles((prev) => {
      if (prev.length >= MAX_LAYER_COUNT) {
        return prev;
      }
      const basePrefix = parsedSupernet ? parsedSupernet.prefix : MIN_SLIDER_PREFIX;
      const sanitised = sanitiseHandles(prev, basePrefix);
      const lastHandle = sanitised[sanitised.length - 1];
      const lastPrefix = lastHandle ? lastHandle.prefix : Math.max(basePrefix, MIN_SLIDER_PREFIX);
      const availableHeadroom = Math.max(0, MAX_SLIDER_PREFIX - lastPrefix);
      const proposedOffset = Math.max(1, Math.ceil(availableHeadroom / 2));
      const proposedPrefix = Math.min(MAX_SLIDER_PREFIX, lastPrefix + proposedOffset);
      const nextLabel = `Layer ${sanitised.length + 1}`;
      const nextHandles = [...sanitised, createHandle(proposedPrefix, nextLabel)];
      return sanitiseHandles(nextHandles, basePrefix);
    });
  };

  // Design agent: Drops the last layer when the user requests a simplification.
  const handleRemoveLayer = () => {
    setLayerHandles((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      const basePrefix = parsedSupernet ? parsedSupernet.prefix : MIN_SLIDER_PREFIX;
      const trimmed = [...prev].sort((a, b) => a.prefix - b.prefix).slice(0, -1);
      return sanitiseHandles(trimmed, basePrefix);
    });
  };

  // Design agent: Records live edits to the label associated with a specific layer.
  const handleLayerLabelChange = (handleId, rawValue) => {
    const sanitised = rawValue.replace(/\s+/g, ' ').slice(0, 40);
    setLayerHandles((prev) =>
      prev.map((handle) =>
        handle.id === handleId
          ? {
            ...handle,
            label: sanitised,
          }
          : handle,
      ),
    );
  };

  // Design agent: Ensures empty labels fall back to a readable default.
  const handleLayerLabelBlur = (handleId) => {
    setLayerHandles((prev) => {
      const index = prev.findIndex((handle) => handle.id === handleId);
      if (index === -1) {
        return prev;
      }
      const current = prev[index];
      const trimmed = (current.label ?? '').trim();
      if (trimmed === current.label && trimmed !== '') {
        return prev;
      }
      const next = [...prev];
      next[index] = {
        ...current,
        label: trimmed === '' ? `Layer ${index + 1}` : trimmed,
      };
      return next;
    });
  };

  // Design agent: Updates the requested network index for a specific layer example.
  const handleExampleLayerChange = (index, rawValue) => {
    const digits = rawValue.replace(/[^\d]/g, '');
    setLayerExampleInputs((prev) => {
      const next = [...prev];
      next[index] = digits;
      return next;
    });
  };

  // Design agent: Stores the requested host index while allowing the user to clear the field.
  const handleExampleHostChange = (rawValue) => {
    const digits = rawValue.replace(/[^\d]/g, '');
    setExampleHostIndex(digits);
  };

  // Design agent: Spec 008 — the "latest ref" pattern: `handleModeChange` and `mode` are re-created
  // every render (they close over baseNetwork/pathSupernet/mode), but the keydown listener below is
  // attached once on mount. Mirroring their latest values into refs on every render lets that single
  // long-lived listener call the freshest version of the sync logic instead of a stale one captured
  // back when the listener was first attached.
  const handleModeChangeRef = useRef(handleModeChange);
  handleModeChangeRef.current = handleModeChange;
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // Design agent: Spec 008 — Alt+1 / Alt+2 switch directly to a method, Alt+M toggles between the
  // two, all without leaving the keyboard. Scoped to fire only while the VLSM ("plan") view is the
  // one currently shown: every top-level view stays mounted in the DOM at once (see App.js's
  // `.spatial-view` active/inactive card stack), so without this check the shortcuts would also
  // trigger while the user is looking at the inspector tab.
  // Developer agent: Reads `event.code` (the physical key), not `event.key` (the character it
  // produces), so Alt+1 fires the same way on AZERTY as on QWERTY even though AZERTY types a
  // different character for that key. Any modifier other than Alt (Ctrl/Shift/Meta) cancels the
  // shortcut so it never collides with a browser or OS binding that also uses Alt plus another key.
  useEffect(() => {
    const handleKeyDown = (event) => {
      const isOnlyAltHeld = event.altKey && !event.ctrlKey && !event.shiftKey && !event.metaKey;
      if (!isOnlyAltHeld) {
        return;
      }

      const isVlsmViewCurrentlyShown = Boolean(
        vlsmRootRef.current?.closest('.spatial-view')?.classList.contains('active'),
      );
      if (!isVlsmViewCurrentlyShown) {
        return;
      }

      if (event.code === 'Digit1') {
        event.preventDefault();
        handleModeChangeRef.current('method1');
      } else if (event.code === 'Digit2') {
        event.preventDefault();
        handleModeChangeRef.current('method2');
      } else if (event.code === 'KeyM') {
        event.preventDefault();
        handleModeChangeRef.current(modeRef.current === 'method1' ? 'method2' : 'method1');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Design agent: Spec 008 — the shortcut hint text itself ("Alt+1"/"Alt+2") is layout-independent,
  // since the listener above binds to the physical key rather than the character a layout types for
  // it. The keyboard-layout hook is still consulted (per spec 008 bullet 3): once a layout is
  // detected the tooltip confirms the same physical key applies on that layout; before detection
  // (the hook still reports 'unknown') a plain static tooltip is shown instead.
  const buildShortcutTooltip = (shortcutLabel) =>
    detectedKeyboardLayout && detectedKeyboardLayout !== 'unknown'
      ? `${shortcutLabel} — same physical key on your detected ${detectedKeyboardLayout.toUpperCase()} layout`
      : shortcutLabel;

  return (
    <div className="column-content" ref={vlsmRootRef}>
      <MethodToggle
        id="vlsm-method-toggle"
        options={[
          {
            label: 'Method 1 · Host sizing',
            value: 'method1',
            shortcutHint: 'Alt+1',
            ariaKeyshortcuts: 'Alt+1',
            shortcutTooltip: buildShortcutTooltip('Alt+1'),
          },
          {
            label: 'Method 2 · Path plan',
            value: 'method2',
            shortcutHint: 'Alt+2',
            ariaKeyshortcuts: 'Alt+2',
            shortcutTooltip: buildShortcutTooltip('Alt+2'),
          },
        ]}
        selectedValue={mode}
        onChange={handleModeChange}
      />

      {mode === 'method1' && (
        <>
          {/* Design agent: Spec 002 — the /31 toggle sits right under the method switcher since it
              only concerns method 1 sizing, using the same checkbox+label pattern already used by
              the method 2 "Readable layer +1" control for visual consistency. */}
          <label className="address-explorer-toggle" id="vlsm-point-to-point-toggle">
            <input
              id="vlsm-point-to-point-toggle-checkbox"
              type="checkbox"
              checked={allowPointToPointLinks}
              onChange={handlePointToPointToggle}
            />
            <span id="vlsm-point-to-point-toggle-label">Point-to-point links as /31 (RFC 3021)</span>
          </label>

          <div className="field-row multi-field">
            <label className="field text-input">
              <span>Supernet</span>
              {/* ip field */}
              <input
                value={baseNetwork}
                onChange={handleBaseChange}
                placeholder="e.g. 192.168.0.0/24"
                id="vlsm-method-one-supernet-input"
                className="field-input"
                autoComplete="off"
              />
            </label>
            <label className="field count-input">
              <span>Gap (Offset)</span>
              <input
                value={gapSize}
                onChange={handleGapChange}
                placeholder="0"
                className="field-input"
                inputMode="numeric"
                pattern="\d*"
                autoComplete="off"
              />
            </label>
          </div>

          <div className="field-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span className="field-group-label" style={{ marginBottom: 0 }}>Usable hosts per subnet</span>
              {/* Design agent: Spec 013 — one Export menu for the whole method 1 plan, mounted right
                  next to the existing sort button since both act on the same "Usable hosts per
                  subnet" results header. Method 2's slider-based layers do not fit the VLSM CSV/BIND
                  row shape (no per-layer "hosts requested"), so the menu only appears here. */}
              <ExportMenu
                id="vlsm-export-menu"
                filenamePrefix="masqip-plan"
                csvHeaders={VLSM_CSV_HEADERS}
                csvRows={vlsmExportRows}
                bindSubnets={vlsmExportSubnets}
              />
              {!inputsAreSorted && (
                <button
                  type="button"
                  className="ghost-button"
                  onClick={handleManualSort}
                  title={
                    methodOneResults.totalWaste > 0
                      ? `Sort subnets by size (largest first)\nUnused space: ${methodOneResults.gaps.join(' + ')} = ${methodOneResults.totalWaste}`
                      : 'Sort subnets by size (largest first)'
                  }
                  style={{ padding: '0.2rem', minWidth: 'auto', height: 'auto', color: 'var(--text-muted)' }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 6h16M4 12h10M4 18h6" />
                  </svg>
                </button>
              )}
            </div>
            {hostInputs.map((entry, index) => (
              <div key={index} className="field field-matrix">
                <input
                  tabIndex={-1}
                  type="text"
                  value={entry.name}
                  onChange={(event) => handleHostChange(index, 'name', event.target.value)}
                  className="result-title result-title-input"
                  placeholder={`Subnet ${index + 1}`}
                  autoComplete="off"
                  style={{
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    marginBottom: '0.25rem',
                    padding: '0',
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    borderBottom: '1px solid transparent',
                    color: 'var(--text-secondary, rgba(255, 255, 255, 0.7))',
                    transition: 'all 0.2s ease',
                  }}
                  onFocus={(e) => {
                    e.target.style.borderBottom = '1px solid var(--primary-color, #4facfe)';
                    e.target.style.color = 'var(--text-primary, #ffffff)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderBottom = '1px solid transparent';
                    e.target.style.color = 'var(--text-secondary, rgba(255, 255, 255, 0.7))';
                  }}
                />
                <div className="field-row">
                  {/* Design agent: Spec 010 — wraps the hosts input so the "↵ Enter" hint chip can be
                      absolutely positioned inside it, the same way IpInputField's BorderTrail wrapper
                      hosts its own hint chip and ghost actions. */}
                  <div
                    className="host-input-wrapper"
                    id={`vlsm-host-input-wrapper-${index}`}
                  >
                    <input
                      ref={(element) => {
                        inputRefs.current[index] = element;
                      }}
                      id={`vlsm-host-input-${index}`}
                      value={entry.hosts}
                      inputMode="numeric"
                      pattern="\d*"
                      placeholder="e.g. 120"
                      className="field-input"
                      onChange={(event) =>
                        handleHostChange(index, 'hosts', event.target.value.replace(/[^\d]/g, ''))
                      }
                      onKeyDown={(event) => handleKeyDown(event, index)}
                      onFocus={() => setActiveHostIndex(index)}
                      onBlur={() => setActiveHostIndex(-1)}
                      autoComplete="off"
                    />
                    {/* Design agent: Spec 010 — same visibility rule as IpInputField: focused, not
                        empty, and only while the list can still grow past this row. */}
                    {activeHostIndex === index
                      && entry.hosts.trim() !== ''
                      && hostInputs.length < MAX_SUBNET_FIELDS && (
                        <EnterHintChip id={`vlsm-host-input-enter-hint-${index}`} />
                    )}
                  </div>
                  <input
                    value={entry.multiplier}
                    inputMode="numeric"
                    pattern="\d*"
                    placeholder="How many ? (1)"
                    className="field-input multiplier-input"
                    onChange={(event) =>
                      handleHostChange(index, 'multiplier', event.target.value.replace(/[^\d]/g, ''))
                    }
                    autoComplete="off"
                    style={{ flex: 1 }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="result-card">
            {methodOneResults.error ? (
              <p className="error-text">{methodOneResults.error}</p>
            ) : methodOneResults.pending ? (
              <p className="result-summary">Provide a base network and host sizes to begin planning.</p>
            ) : (
              <>
                <div className="result-metric-grid">
                  <div className="result-metric">
                    <span className="result-metric-label">Base Capacity</span>
                    <span className="result-metric-value">{methodOneResults.usableCapacity.toLocaleString()} <span style={{ fontSize: '0.8em', opacity: 0.7, fontWeight: 400 }}>addrs</span></span>
                  </div>
                  <div className="result-metric">
                    <span className="result-metric-label">Subnets Created</span>
                    <span className="result-metric-value">{methodOneResults.totalSubnets.toLocaleString()}</span>
                  </div>
                  <div className="result-metric" style={{ gridColumn: '1 / -1' }}>
                    <span className="result-metric-label">Usable Range</span>
                    <span className="result-metric-value" style={{ fontSize: '0.95rem', fontFamily: 'var(--font-mono, monospace)' }}>
                      {methodOneResults.usableRange.start} — {methodOneResults.usableRange.end}
                    </span>
                  </div>
                </div>
                <ul className="result-list">
                  {methodOneResults.groups.map((group, index) => (
                    <li key={index} className="result-item">
                      {(() => {
                        const allocations = group.allocations;
                        const isExpanded = expandedGroups.includes(index);
                        const visibleAllocations = isExpanded ? allocations : allocations.slice(0, 4);
                        const hasHiddenAllocations = allocations.length > visibleAllocations.length;

                        if (allocations.length === 0) {
                          return <span className="result-title muted">Awaiting size…</span>;
                        }

                        const groupColor = LAYER_PALETTE[index % LAYER_PALETTE.length];

                        return (
                          <>
                            <div className="result-title-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                              <span className="layer-colour-chip" style={{ backgroundColor: groupColor, width: '0.75rem', height: '0.75rem' }} />
                              <input
                                type="text"
                                value={group.name}
                                onChange={(event) => handleHostChange(index, 'name', event.target.value)}
                                className="result-title result-title-input"
                                placeholder={`Subnet group ${index + 1}`}
                                autoComplete="off"
                              />
                              <span className="result-title" style={{ opacity: 0.6 }}>
                                ({allocations.length}×)
                              </span>
                            </div>
                            <ul className="result-sublist">
                              {visibleAllocations.map((allocation, instanceIndex) => {
                                // Design agent: Spec 002 — a /31 has no reserved network/broadcast
                                // (RFC 3021), so its row swaps the usual "hosts requested" badge and
                                // "Broadcast" detail for a "P2P" label and the two usable addresses.
                                const isPointToPoint = allocation.prefix === 31;
                                return (
                                  <li key={instanceIndex} className="result-subitem result-subitem--card" id={`method1-subnet-${index}-${instanceIndex}`}>
                                    <div className="subitem-header">
                                      <span className="subitem-index">#{instanceIndex + 1}</span>
                                      <span className="subitem-network">
                                        {intToIpv4(allocation.network)}
                                        <span className="subitem-prefix">/{allocation.prefix}</span>
                                      </span>
                                      <span className="subitem-badge" id={`method1-subnet-${index}-${instanceIndex}-badge`}>
                                        {isPointToPoint ? 'P2P · 2 usable' : `${allocation.hosts} hosts`}
                                      </span>
                                    </div>
                                    <div className="subitem-details">
                                      {isPointToPoint ? (
                                        <div className="detail-col" id={`method1-subnet-${index}-${instanceIndex}-p2p-hosts`}>
                                          <span className="detail-label">P2P hosts</span>
                                          <span className="detail-value">
                                            {intToIpv4(allocation.network)} &amp; {intToIpv4(allocation.network + 1)}
                                          </span>
                                        </div>
                                      ) : (
                                        <div className="detail-col">
                                          <span className="detail-label">Broadcast</span>
                                          <span className="detail-value">{intToIpv4(allocation.broadcast)}</span>
                                        </div>
                                      )}
                                      <div className="detail-col">
                                        <span className="detail-label">Mask</span>
                                        <span className="detail-value">{formatMask(maskFromPrefix(allocation.prefix))}</span>
                                      </div>
                                      <div className="detail-col">
                                        <span className="detail-label">Block Size</span>
                                        <span className="detail-value">{allocation.blockSize}</span>
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                            {hasHiddenAllocations && (
                              <button
                                type="button"
                                className="ghost-button expand-button"
                                onClick={() => toggleGroupExpansion(index)}
                              >
                                {isExpanded
                                  ? 'Show fewer plans'
                                  : `Show all ${allocations.length.toLocaleString()} plans`}
                              </button>
                            )}
                          </>
                        );
                      })()}
                    </li>
                  ))}
                </ul>
                {/* Design agent: Spec 007 — the pool usage summary sits right under the subnet
                    list, since it reports on the same plan the operator just reviewed above. */}
                <PoolUsage
                  id="vlsm-pool-usage"
                  networkCapacity={methodOneResults.poolUsage.networkCapacity}
                  allocatedAddresses={methodOneResults.poolUsage.allocatedAddresses}
                  remainingAddresses={methodOneResults.poolUsage.remainingAddresses}
                  usagePercent={methodOneResults.poolUsage.usagePercent}
                  freeBlocks={methodOneResults.poolUsage.freeBlocks}
                  overflowAddresses={methodOneResults.poolUsage.overflowAddresses}
                />
              </>
            )}
          </div>
        </>
      )}

      {mode === 'method2' && (
        <>
          <label className="field">
            <span>Supernet</span>
            {/* ip field */}
            <input
              value={pathSupernet}
              onChange={handleSupernetChange}
              placeholder="e.g. 10.0.0.0/8"
              id="vlsm-method-two-supernet-input"
              className="field-input"
              autoComplete="off"
            />
          </label>

          {/* Design agent: Spec 009 — the zoom trigger sits directly above the slider it expands, so
              its purpose ("give this exact control more room") is obvious without a caption. */}
          <div className="slider-zoom-trigger-row" id="method-two-slider-zoom-trigger-row">
            <button
              type="button"
              className="ghost-button slider-zoom-open-button"
              id="method-two-slider-zoom-open-button"
              onClick={handleOpenSliderZoom}
              disabled={!parsedSupernet || Boolean(methodTwoPlan.error)}
              aria-label="Expand the layer distribution slider into a fullscreen zoom view"
            >
              <span aria-hidden="true">⤢</span> Zoom
            </button>
          </div>

          <HierarchicalSlider
            supernetPrefix={sliderPreview.basePrefix}
            handles={sliderPreview.handles}
            magnets={MAGNET_PREFIXES}
            onHandleChange={handleLayerChange}
            onAddLayer={handleAddLayer}
            onRemoveLayer={handleRemoveLayer}
            canAdd={layerHandles.length < MAX_LAYER_COUNT}
            canRemove={layerHandles.length > 1}
            disabled={!parsedSupernet || Boolean(methodTwoPlan.error)}
          />

          {/* Design agent: Spec 009 — the overlay mounts unconditionally (it returns null while
              closed) so its own Escape-key listener and portal lifecycle stay simple; every prop
              below is state/data VlsmCalculator already computed for the inline card above, passed
              through unchanged so the zoom view can never drift out of sync with it. */}
          <SliderZoomOverlay
            id="method-two-slider-zoom-overlay"
            isOpen={isSliderZoomOpen}
            onClose={handleCloseSliderZoom}
            sliderProps={{
              supernetPrefix: sliderPreview.basePrefix,
              handles: sliderPreview.handles,
              magnets: MAGNET_PREFIXES,
              onHandleChange: handleLayerChange,
              onAddLayer: handleAddLayer,
              onRemoveLayer: handleRemoveLayer,
              canAdd: layerHandles.length < MAX_LAYER_COUNT,
              canRemove: layerHandles.length > 1,
              disabled: !parsedSupernet || Boolean(methodTwoPlan.error),
            }}
            layers={methodTwoPlan.layers ?? []}
            onLayerLabelChange={handleLayerLabelChange}
            onLayerLabelBlur={handleLayerLabelBlur}
            planError={methodTwoPlan.error}
            planPending={methodTwoPlan.pending}
            addressFormulaRows={addressFormulaRows}
            addressFormulaFontSize={addressFormulaFontSize}
            layerExampleInputs={layerExampleInputs}
            onExampleLayerChange={handleExampleLayerChange}
            exampleHostIndex={exampleHostIndex}
            onExampleHostChange={handleExampleHostChange}
            exampleAnalysis={exampleAnalysis}
            exampleDisplaySegments={exampleDisplaySegments}
            hostAddresses={methodTwoPlan.host?.hostAddresses ?? 0}
          />

          <div className="result-card">
            {methodTwoPlan.error ? (
              <p className="error-text">{methodTwoPlan.error}</p>
            ) : methodTwoPlan.pending ? (
              <p className="result-summary">
                Start by selecting a supernet, then drag the sliders to carve your addressing layers.
              </p>
            ) : (
              <>
                <div className="result-subheading result-subheading--formula">Address format</div>
                {addressFormulaRows.length === 0 ? (
                  <p className="result-meta muted formula-placeholder">Adjust the slider to generate a readable formula.</p>
                ) : (
                  <div
                    className="cidr-display cidr-display--formula"
                    aria-label="Address format"
                    style={{ '--formula-font-size': addressFormulaFontSize }}
                  >
                    <span className="cidr-prefix">Formula</span>
                    <div className="cidr-octets">
                      {addressFormulaRows.map((row, rowIndex) => (
                        <span key={`formula-row-${rowIndex}`} className="cidr-row">
                          {row.map((segment) => (
                            <span key={segment.key} className="cidr-segment" style={segment.style}>
                              {segment.value}
                            </span>
                          ))}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <p className="result-note">
                  IPv6 readability tip: keep decisive half-octets outside the mask so each hexadecimal character
                  remains visible.
                </p>
                {/* Design agent: Remove Key Metrics section as requested and surface the layer breakdown before exploration tools. */}
                <div className="result-subheading" id="method-two-layer-breakdown-heading">
                  Layer breakdown
                </div>
                <ul className="result-list compact" id="method-two-layer-breakdown">
                  {methodTwoPlan.layers.map((layer) => {
                    const layerColour = LAYER_PALETTE[layer.index % LAYER_PALETTE.length];
                    const isReadable = layer.isReadable;
                    const layerLabelValue = layer.label ?? '';
                    const trimmedLayerLabel = layerLabelValue.trim();
                    const fallbackLayerLabel = `Layer ${layer.index + 1}`;
                    const readableLayerLabel = trimmedLayerLabel === '' ? fallbackLayerLabel : trimmedLayerLabel;
                    return (
                      <li key={layer.id} className="result-item layer-result-item">
                        <div className="layer-result-header">
                          <span className="layer-colour-chip" style={{ backgroundColor: layerColour }} />
                          <input
                            type="text"
                            value={layerLabelValue}
                            onChange={(event) => handleLayerLabelChange(layer.id, event.target.value)}
                            onBlur={() => handleLayerLabelBlur(layer.id)}
                            className="result-title result-title-input"
                            aria-label={`Rename ${readableLayerLabel}`}
                            placeholder={fallbackLayerLabel}
                            autoComplete="off"
                          />
                          <span className={`readable-tag ${isReadable ? 'is-readable' : 'is-unreadable'}`}>
                            Readable: {isReadable ? 'Yes' : 'No'}
                          </span>
                        </div>
                        {/* Design agent: result-meta removed per requirements. */}
                      </li>
                    );
                  })}
                </ul>
                {/* Design agent: Address Explorer follows the breakdown for contextual flow. */}
                <details className="ip-examples" id="method-two-address-explorer">
                  <summary id="method-two-address-explorer-summary">
                    <span id="method-two-address-explorer-title">Address explorer</span>
                    <label
                      className="address-explorer-toggle"
                      id="method-two-readable-offset"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <input
                        id="method-two-readable-offset-checkbox"
                        type="checkbox"
                        checked={applyReadableOffsets}
                        onChange={(event) => {
                          event.stopPropagation();
                          handleReadableOffsetChange(event);
                        }}
                        onClick={(event) => event.stopPropagation()}
                      />
                      <span id="method-two-readable-offset-label">Readable layer +1</span>
                    </label>
                  </summary>
                  <div className="ip-examples-body">
                    <p className="ip-examples-intro">
                      Choose network indices per layer and optionally a host index to preview the resulting address.
                    </p>
                    <div className="ip-examples-grid">
                      {methodTwoPlan.layers.map((layer, index) => {
                        const value = layerExampleInputs[index] ?? '';
                        const exampleSummary = exampleAnalysis?.layerSummaries?.[index];
                        const displayLabel =
                          layer.label && layer.label.trim() !== '' ? layer.label.trim() : `Layer ${index + 1}`;
                        return (
                          <label key={layer.id} className="field ip-examples-field">
                            <span>
                              {displayLabel} network{' '}
                              <span
                                className="layer-colour-chip inline"
                                style={{ backgroundColor: LAYER_PALETTE[index % LAYER_PALETTE.length] }}
                              />
                            </span>
                            <input
                              value={value}
                              onChange={(event) => handleExampleLayerChange(index, event.target.value)}
                              placeholder="e.g. 5"
                              className="field-input"
                              inputMode="numeric"
                              aria-describedby={`layer-${layer.id}-hint`}
                              autoComplete="off"
                            />
                            <span id={`layer-${layer.id}-hint`} className="field-hint">
                              Up to {layer.networkCount.toLocaleString()} networks
                            </span>
                            {exampleSummary && (
                              <span className="field-hint" id={`layer-${layer.id}-resolved`}>
                                Resolves to {exampleSummary.formattedNetwork}/{layer.prefix}
                                {exampleSummary.appliedOffset !== 0 && (
                                  <span className="field-hint-offset" id={`layer-${layer.id}-offset`}>
                                    {' '}(offset +{exampleSummary.appliedOffset})
                                  </span>
                                )}
                              </span>
                            )}
                          </label>
                        );
                      })}
                      <label className="field ip-examples-field">
                        <span>Host index</span>
                        <input
                          value={exampleHostIndex}
                          onChange={(event) => handleExampleHostChange(event.target.value)}
                          placeholder="e.g. 23"
                          className="field-input"
                          inputMode="numeric"
                          aria-describedby="host-index-hint"
                          autoComplete="off"
                        />
                        <span id="host-index-hint" className="field-hint">
                          Up to {methodTwoPlan.host.hostAddresses.toLocaleString()} addresses
                        </span>
                      </label>
                    </div>
                    {exampleAnalysis && (
                      <div className="ip-examples-result">
                        <div className="cidr-display" aria-label="Resolved example address">
                          <span className="cidr-prefix">Result</span>
                          <span className="cidr-octets">
                            {exampleDisplaySegments.map((segment) => (
                              <span
                                key={segment.key}
                                className="cidr-segment"
                                style={segment.style}
                                data-role={segment.role}
                              >
                                {segment.value}
                              </span>
                            ))}
                          </span>
                          <span className="cidr-separator">/</span>
                          <span className="cidr-segment" style={{ color: CIDR_COLORS.network }}>
                            {exampleAnalysis.networkPrefix}
                          </span>
                        </div>
                        <p className="result-meta">
                          Host #{exampleAnalysis.hostIndex.toLocaleString()} within network{' '}
                          {intToIpv4(exampleAnalysis.networkAddress)}/{exampleAnalysis.networkPrefix}
                        </p>
                      </div>
                    )}
                  </div>
                </details>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default VlsmCalculator;
// Design agent: Also export named for import flexibility and IDE refactors.
export { VlsmCalculator };










